import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { isDemoSession, readTourState, writeTourState } from '@services/demo/demoAccount';
import { OWNER_TOUR_STEPS } from './ownerTourSteps';

type Box = { top: number; left: number; width: number; height: number };

const clipBox = (rect: DOMRect): Box | null => {
  const top = Math.max(rect.top, 8);
  const left = Math.max(rect.left, 8);
  const right = Math.min(rect.right, window.innerWidth - 8);
  const bottom = Math.min(rect.bottom, window.innerHeight - 8);
  const width = right - left;
  const height = Math.min(bottom - top, window.innerHeight * 0.62);
  if (width < 24 || height < 24) return null;
  return { top, left, width, height };
};

const OwnerGuideTour = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [spot, setSpot] = useState<Box | null>(null);
  const onAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (!isDemoSession() || !onAdmin) {
      setOpen(false);
      return;
    }
    const saved = readTourState();
    if (saved === 'done') {
      setOpen(false);
      return;
    }
    const savedIndex = Number(saved);
    const nextIndex =
      Number.isFinite(savedIndex) && savedIndex >= 0 && savedIndex < OWNER_TOUR_STEPS.length
        ? savedIndex
        : 0;
    setIndex(nextIndex);
    setOpen(true);
  }, [onAdmin, location.pathname]);

  const step = OWNER_TOUR_STEPS[index];

  useEffect(() => {
    if (!open || !step) return;
    if (location.pathname !== step.path) navigate(step.path);
  }, [open, step, location.pathname, navigate]);

  useEffect(() => {
    if (!open || !step || location.pathname !== step.path) return undefined;
    let stopped = false;
    let scrolled = false;

    const measure = () => {
      if (stopped) return;
      if (!step.target) {
        setSpot((prev) => (prev === null ? prev : null));
        return;
      }
      const el = document.querySelector(step.target);
      if (!el) {
        setSpot((prev) => (prev === null ? prev : null));
        return;
      }
      if (!scrolled) {
        el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        scrolled = true;
      }
      const next = clipBox(el.getBoundingClientRect());
      setSpot((prev) => {
        if (!next) return prev === null ? prev : null;
        if (
          prev &&
          prev.top === next.top &&
          prev.left === next.left &&
          prev.width === next.width &&
          prev.height === next.height
        ) {
          return prev;
        }
        return next;
      });
    };

    measure();
    const timer = window.setInterval(measure, 400);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, step, location.pathname]);

  const finish = () => {
    writeTourState('done');
    setOpen(false);
  };

  const go = (next: number) => {
    const clamped = Math.max(0, Math.min(OWNER_TOUR_STEPS.length - 1, next));
    writeTourState(String(clamped));
    setIndex(clamped);
    setOpen(true);
  };

  const replay = () => {
    writeTourState('0');
    setIndex(0);
    setOpen(true);
    if (location.pathname !== OWNER_TOUR_STEPS[0].path) navigate(OWNER_TOUR_STEPS[0].path);
  };

  const showReplay = isDemoSession() && onAdmin && !open;
  const cardWidth = Math.min(380, window.innerWidth - 24);
  let cardTop = Math.max(24, (window.innerHeight - 240) / 2);
  let cardLeft = Math.max(12, (window.innerWidth - cardWidth) / 2);
  if (spot) {
    const below = spot.top + spot.height + 12;
    cardTop = below + 240 > window.innerHeight ? Math.max(8, spot.top - 248) : below;
    cardLeft = Math.min(Math.max(12, spot.left), window.innerWidth - cardWidth - 12);
  }

  return createPortal(
    <>
      {showReplay && (
        <button type="button" className="owner_tour_replay" onClick={replay}>
          화면 안내
        </button>
      )}
      {open && step && (
        <div className="owner_tour" role="dialog" aria-modal="true" aria-labelledby="owner_tour_title">
          <div className={`owner_tour_catcher ${spot ? 'is_clear' : ''}`} />
          {spot && <div className="owner_tour_spot" style={spot} />}
          <section className="owner_tour_card" style={{ top: cardTop, left: cardLeft, width: cardWidth }}>
            <p className="owner_tour_progress">
              {index + 1} / {OWNER_TOUR_STEPS.length}
            </p>
            <h2 id="owner_tour_title">{step.title}</h2>
            <p className="owner_tour_body">{step.body}</p>
            <div className="owner_tour_actions">
              <button type="button" className="owner_tour_text" onClick={finish}>
                건너뛰기
              </button>
              <div className="owner_tour_nav">
                <button type="button" onClick={() => go(index - 1)} disabled={index === 0}>
                  이전
                </button>
                {index === OWNER_TOUR_STEPS.length - 1 ? (
                  <button type="button" className="is_primary" onClick={finish}>
                    마치기
                  </button>
                ) : (
                  <button type="button" className="is_primary" onClick={() => go(index + 1)}>
                    다음
                  </button>
                )}
              </div>
            </div>
          </section>
        </div>
      )}
    </>,
    document.body,
  );
};

export default OwnerGuideTour;
